import React from 'react';
import { NavLink } from 'react-router-dom';
import { User, UserRole } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import SalonSelector from './SalonSelector';
import './Sidebar.css';

interface SidebarProps {
    isCollapsed: boolean;
    isMobile: boolean;
    isSidebarOpen: boolean;
    toggleSidebar: () => void;
    closeSidebar: () => void;
    currentUser: User | null;
    canAccessEmployees: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
    isCollapsed,
    isMobile,
    isSidebarOpen,
    toggleSidebar,
    closeSidebar,
    currentUser,
    canAccessEmployees
}) => {
    const { t, setLanguage, language } = useLanguage();

    // Handle nav click (close sidebar on mobile)
    const handleNavClick = () => {
        if (isMobile) {
            closeSidebar();
        }
    };

    // Helper to get user role text
    const getUserRoleText = (role: UserRole | string) => {
        switch (role) {
            case UserRole.OWNER:   return t('roles.owner');
            case UserRole.MANAGER: return t('roles.manager');
            case UserRole.STAFF:   return t('roles.staff');
            case UserRole.RECEPTIONIST: return t('roles.receptionist');
            case UserRole.ADMIN:   return t('roles.admin');
            default:               return t('roles.staff');
        }
    };

    return (
        <>
            <div className="sidebar">
                <div className="sidebar-header">
                    {(!isCollapsed || isMobile) && <h2>{t('sidebar.dashboard')}</h2>}

                    {/* Only show toggle button on desktop */}
                    {!isMobile && (
                        <button
                            className="sidebar-toggle"
                            onClick={toggleSidebar}
                            aria-label={isCollapsed ? t('sidebar.openMenu') : t('sidebar.closeMenu')}
                        >
                            <span className="hamburger-icon">
                                <span className={`hamburger-line ${isCollapsed ? '' : 'active'}`}></span>
                                <span className={`hamburger-line ${isCollapsed ? '' : 'active'}`}></span>
                                <span className={`hamburger-line ${isCollapsed ? '' : 'active'}`}></span>
                            </span>
                        </button>
                    )}
                </div>

                {/* Salon Selector - visible only to owners/admins inside the component logic */}
                <SalonSelector />

                <nav className="sidebar-nav">
                    <NavLink
                        to="/dashboard"
                        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        onClick={handleNavClick}
                    >
                        <span className="nav-icon">📊</span>
                        <span className="nav-text">{t('sidebar.dashboard')}</span>
                    </NavLink>

                    <NavLink
                        to="/bookings"
                        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        onClick={handleNavClick}
                    >
                        <span className="nav-icon">📋</span>
                        <span className="nav-text">{t('sidebar.bookings')}</span>
                    </NavLink>

                    <NavLink
                        to="/notifications"
                        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        onClick={handleNavClick}
                    >
                        <span className="nav-icon">🔔</span>
                        <span className="nav-text">{t('notifications.title') || 'Notifications'}</span>
                    </NavLink>

                    {/* Only show Employees if user has permission */}
                    {canAccessEmployees && (
                        <NavLink
                            to="/employees"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            onClick={handleNavClick}
                        >
                            <span className="nav-icon">👥</span>
                            <span className="nav-text">{t('sidebar.employees')}</span>
                        </NavLink>
                    )}

                    {/* Customers - Visible to all staff/manager/owner */}
                    {currentUser && [UserRole.OWNER as string, UserRole.MANAGER as string, UserRole.STAFF as string].includes(currentUser.role) && (
                        <NavLink
                            to="/customers"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            onClick={handleNavClick}
                        >
                            <span className="nav-icon">👥</span>
                            <span className="nav-text">{t('sidebar.customers') || 'Customers'}</span>
                        </NavLink>
                    )}

                    {/* Business Settings - Owner/Manager only */}
                    {currentUser && [UserRole.OWNER as string, UserRole.MANAGER as string].includes(currentUser.role) && (
                        <NavLink
                            to="/settings/business"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            onClick={handleNavClick}
                        >
                            <span className="nav-icon">⚙️</span>
                            <span className="nav-text">{t('sidebar.settings') || 'Settings'}</span>
                        </NavLink>
                    )}

                    {/* Reviews - Owner/Manager only */}
                    {currentUser && [UserRole.OWNER as string, UserRole.MANAGER as string, 'admin'].includes(currentUser.role) && (
                        <NavLink
                            to="/reviews"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            onClick={handleNavClick}
                        >
                            <span className="nav-icon">⭐</span>
                            <span className="nav-text">{language === 'vi' ? 'Đánh giá' : 'Reviews'}</span>
                        </NavLink>
                    )}

                    {/* Earnings - All Staff (Controller handles permissions) */}
                    {currentUser && [UserRole.OWNER as string, UserRole.MANAGER as string, UserRole.STAFF as string].includes(currentUser.role) && (
                        <NavLink
                            to="/earnings"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            onClick={handleNavClick}
                        >
                            <span className="nav-icon">💰</span>
                            <span className="nav-text">{t('sidebar.earnings')}</span>
                        </NavLink>
                    )}

                    {/* Admin — Business Requests (owner-only shortcut) */}
                    {currentUser && currentUser.role === UserRole.OWNER && (
                        <NavLink
                            to="/admin/requests"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            onClick={handleNavClick}
                        >
                            <span className="nav-icon">📋</span>
                            <span className="nav-text">{language === 'vi' ? 'Yêu cầu Business' : 'Business Requests'}</span>
                        </NavLink>
                    )}

                    <NavLink
                        to="/profile"
                        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        onClick={handleNavClick}
                    >
                        <span className="nav-icon">👤</span>
                        <span className="nav-text">{t('sidebar.profile')}</span>
                    </NavLink>
                </nav>

                <div className="sidebar-footer">
                    {currentUser && (
                        <div className="user-info">
                            <div className="user-avatar">
                                {currentUser.name.charAt(0).toUpperCase()}
                            </div>
                            {(!isCollapsed || isMobile) && (
                                <div className="user-details">
                                    <p className="user-name">{currentUser.name}</p>
                                    <p className="user-role">{getUserRoleText(currentUser.role)}</p>
                                    <p className="user-status">{t('employees.status.active')}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Overlay for mobile */}
            {isMobile && isSidebarOpen && (
                <div className="sidebar-overlay" onClick={closeSidebar} />
            )}
        </>
    );
};

export default Sidebar;
